import type { FormEvent } from 'react'
import { useCallback, useState } from 'react'
import {
  suggestSunoStylePrompts,
  type SunoStylePromptGenerationMode,
  type SunoStylePromptHistoryItem,
  type SunoStylePromptSuggestion
} from '../../../api/suno'
import { selectedPromptToSnapshot } from './stylePromptGeneratorTypes'
import { useGeneratedPromptSave } from './useGeneratedPromptSave'
import { useStylePromptHistory } from './useStylePromptHistory'
import { useStylePromptLibrary } from './useStylePromptLibrary'

type UseStylePromptGeneratorDataOptions = {
  toast: (message: string) => void
}

export const useStylePromptGeneratorData = ({ toast }: UseStylePromptGeneratorDataOptions) => {
  const [styleDescription, setStyleDescription] = useState('')
  const [styleGenerationMode, setStyleGenerationMode] = useState<SunoStylePromptGenerationMode>('free')
  const [styleSuggestions, setStyleSuggestions] = useState<SunoStylePromptSuggestion[]>([])
  const [styleError, setStyleError] = useState<string | null>(null)
  const [styleLoading, setStyleLoading] = useState(false)

  const {
    styleHistory,
    styleHistoryDeletingId,
    styleHistoryError,
    styleHistoryLoading,
    styleHistoryOpen,
    deleteStyleHistoryItem,
    setStyleHistory,
    setStyleHistoryOpen
  } = useStylePromptHistory({ toast })

  const clearStyleError = useCallback((): void => {
    setStyleError(null)
  }, [])

  const {
    selectedBasePrompt,
    promptLibraryError,
    promptLibraryLoading,
    promptLibraryOpen,
    promptLibraryPrompts,
    promptLibrarySearch,
    clearSelectedBasePrompt,
    loadPromptLibrary,
    openPromptLibrary,
    selectPromptLibraryPrompt,
    setPromptLibraryOpen,
    setPromptLibrarySearch,
    setSelectedBasePrompt
  } = useStylePromptLibrary({
    onBasePromptLoaded: clearStyleError,
    onBasePromptLoadError: setStyleError
  })

  const {
    sunoPromptGroups,
    promptEditor,
    promptSaving,
    openGeneratedPromptSaveDialog,
    savePrompt,
    setPromptEditor,
    suggestPromptMetadata
  } = useGeneratedPromptSave({ toast })

  const styleDescriptionValid = styleDescription.trim().length > 0
  const styleClearAvailable = styleDescription.trim().length > 0 || styleSuggestions.length > 0 || styleError !== null || selectedBasePrompt !== null

  const clearSunoStylePromptWorkspace = useCallback((): void => {
    setStyleDescription('')
    setStyleGenerationMode('free')
    setStyleSuggestions([])
    setStyleError(null)
    clearSelectedBasePrompt()
  }, [clearSelectedBasePrompt])

  const submitSunoStylePrompts = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()

    const description = styleDescription.trim()

    if (!description) {
      setStyleError('Description is required.')
      return
    }

    setStyleLoading(true)
    setStyleError(null)

    try {
      const result = await suggestSunoStylePrompts({
        description,
        generation_mode: styleGenerationMode,
        base_prompt_id: selectedBasePrompt?.source === 'library' ? selectedBasePrompt.id : undefined,
        base_prompt: selectedBasePrompt?.source === 'history' ? selectedPromptToSnapshot(selectedBasePrompt) : undefined
      })

      setStyleSuggestions(result.items)
      if (result.history_item) {
        setStyleHistory((items) => [
          result.history_item!,
          ...items.filter((item) => item.id !== result.history_item!.id)
        ].slice(0, 50))
      }
    } catch (requestError) {
      setStyleError(requestError instanceof Error ? requestError.message : 'Unable to generate Suno style prompts.')
    } finally {
      setStyleLoading(false)
    }
  }

  const openStyleHistoryItem = (item: SunoStylePromptHistoryItem): void => {
    setStyleDescription(item.description)
    setStyleGenerationMode(item.generation_mode ?? 'free')
    setStyleSuggestions(item.result_json)
    setSelectedBasePrompt(item.base_prompt_snapshot ? { ...item.base_prompt_snapshot, source: 'history' } : null)
    setStyleError(null)
    setStyleHistoryOpen(false)
  }

  return {
    styleDescription,
    setStyleDescription,
    styleGenerationMode,
    setStyleGenerationMode,
    styleSuggestions,
    styleError,
    styleLoading,
    styleHistory,
    styleHistoryLoading,
    styleHistoryError,
    styleHistoryDeletingId,
    styleHistoryOpen,
    selectedBasePrompt,
    promptLibraryOpen,
    promptLibrarySearch,
    promptLibraryPrompts,
    promptLibraryLoading,
    promptLibraryError,
    sunoPromptGroups,
    promptEditor,
    promptSaving,
    styleDescriptionValid,
    styleClearAvailable,
    clearSelectedBasePrompt,
    clearSunoStylePromptWorkspace,
    deleteStyleHistoryItem,
    loadPromptLibrary,
    openGeneratedPromptSaveDialog,
    openPromptLibrary,
    openStyleHistory: () => setStyleHistoryOpen(true),
    openStyleHistoryItem,
    savePrompt,
    selectPromptLibraryPrompt,
    setPromptEditor,
    setPromptLibraryOpen,
    setPromptLibrarySearch,
    setStyleHistoryOpen,
    submitSunoStylePrompts,
    suggestPromptMetadata
  }
}
